import React, { useEffect, useMemo, useState } from 'react';
import { ListIcon, MapIcon, RotateCwIcon } from 'lucide-react';
import type { City, Sport } from '../../types/landing';
import type { SportInventory } from '../../hooks/useSportInventory';
import { ContentNote } from '../ContentNote';
import { SectionHeading } from '../SectionHeading';
import { VenueCard } from './VenueCard';
import { VenueMap } from './VenueMap';
interface FindNearYouProps {
  sport: Sport;
  city: City;
  inventory: SportInventory;
}
export function FindNearYou({
  sport,
  city,
  inventory
}: FindNearYouProps) {
  const [areaFilter, setAreaFilter] = useState('all');
  const [openOnly, setOpenOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<'list' | 'map'>('list');
  useEffect(() => {
    setAreaFilter('all');
    setOpenOnly(false);
    setSelectedId(null);
  }, [sport.slug, city.slug]);
  const venues = useMemo(() => inventory.areas.flatMap(a => a.venues).filter(v => areaFilter === 'all' || v.areaId === areaFilter).filter(v => !openOnly || v.sportPrograms.some(p => p.status === 'open')), [inventory.areas, areaFilter, openOnly]);
  const select = (id: string) => {
    setSelectedId(id);
    document.getElementById(`venue-${id}`)?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth'
    });
  };
  const chips = [{
    id: 'all',
    label: 'All areas'
  }, ...inventory.areas.map(a => ({
    id: a.area.id,
    label: a.area.name
  }))];
  return <section id="find-near-you" aria-labelledby="find-heading" className="relative scroll-mt-4 border-t border-brand-line bg-white pt-12 md:pt-16" data-id="element-46" data-mp="osixva2objt0">
      <div className="px-5 md:px-10" data-id="element-47" data-mp="xmnj9dz1g315">
        <SectionHeading id="find-heading" data-id="element-48" data-mp="xnp7kk52em0t">Find {sport.name} Near You</SectionHeading>
        <p className="mx-auto mt-3 max-w-2xl text-center text-[15px] text-brand-muted" data-id="element-49" data-mp="1ckzh3ny5dhtw">
          {inventory.status === 'ready' ? `${inventory.venueCount} locations offer i9 Sports ${sport.noun} across the ${city.name} area. Pick the one closest to home.` : `Loading ${sport.noun} locations across the ${city.name} area…`}
        </p>

        <div className="mt-8 flex flex-col gap-3 md:flex-row md:items-center md:justify-between" data-id="element-50" data-mp="1omqzcb0ncfq6">
          <div role="group" aria-label="Filter by area" className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1 md:mx-0 md:flex-wrap md:px-0" data-id="element-51" data-mp="1tcxoqd2zg49h">
            {chips.map(c => <button key={c.id} type="button" onClick={() => {
            setAreaFilter(c.id);
            setSelectedId(null);
          }} aria-pressed={areaFilter === c.id} className={`whitespace-nowrap rounded-sm border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${areaFilter === c.id ? 'border-brand-sky bg-brand-sky text-white' : 'border-brand-line bg-white text-brand-navy hover:border-brand-sky'}`} data-id="element-52" data-mp="1yemxtn6na7ux">
                {c.label}
              </button>)}
          </div>
          <label className="flex shrink-0 cursor-pointer items-center gap-2 text-sm text-brand-ink" data-id="element-53" data-mp="5nt2lpaz5fzs">
            <input type="checkbox" checked={openOnly} onChange={e => setOpenOnly(e.target.checked)} className="h-4 w-4 accent-brand-red" data-id="element-54" data-mp="opxy5ywpbsc" />
            Registration open now
          </label>
        </div>

        <div className="mt-4 grid grid-cols-2 rounded-sm border border-brand-line p-1 md:hidden" role="tablist" aria-label="View" data-id="element-55" data-mp="td3wqnxtkc4y">
          {(['list', 'map'] as const).map(v => <button key={v} type="button" role="tab" aria-selected={mobileView === v} onClick={() => setMobileView(v)} className={`flex items-center justify-center gap-1.5 rounded-sm py-2 text-sm font-semibold transition-colors duration-150 ${mobileView === v ? 'bg-brand-navy text-white' : 'text-brand-navy'}`} data-id="element-56" data-mp="ocg3rwq15l83">
              {v === 'list' ? <ListIcon className="h-4 w-4" aria-hidden="true" data-id="element-57" data-mp="rulqrcp9r9gm" /> : <MapIcon className="h-4 w-4" aria-hidden="true" data-id="element-58" data-mp="rt2ahm3m4yeu" />}
              {v === 'list' ? 'List' : 'Map'}
            </button>)}
        </div>
      </div>

      <div className="mt-5 grid border-y border-brand-line md:grid-cols-12" data-id="element-59" data-mp="hy0p91cgkghj">
        <div className={`${mobileView === 'map' ? 'block' : 'hidden'} h-[420px] bg-brand-mist md:col-span-7 md:block md:h-[560px]`} data-id="element-60" data-mp="mviuagncubk9">
          {inventory.status === 'loading' ? <div className="h-full w-full animate-pulse bg-brand-line/60" aria-label="Loading map" data-id="element-61" data-mp="1fkg3lu9l8y0v" /> : <VenueMap venues={venues} center={city.center} selectedId={selectedId} onSelect={select} visibleKey={`${mobileView}-${venues.length}`} data-id="element-62" data-mp="10nyw5e4is3jl" />}
        </div>

        <div className={`${mobileView === 'list' ? 'block' : 'hidden'} md:col-span-5 md:block md:h-[560px] md:overflow-y-auto md:border-l md:border-brand-line`} data-id="element-63" data-mp="3ldo7vi9xf982">
          {inventory.status === 'loading' && <ul aria-label="Loading locations" className="divide-y divide-brand-line" data-id="element-64" data-mp="28ew3ux7yr61r">
              {[0, 1, 2, 3].map(i => <li key={i} className="space-y-2 px-5 py-5" data-id="element-65" data-mp="23f01f2ewzmob">
                  <div className="h-4 w-2/3 animate-pulse rounded-sm bg-brand-line" data-id="element-66" data-mp="2hfki9t19r8f6" />
                  <div className="h-3 w-1/2 animate-pulse rounded-sm bg-brand-line" data-id="element-67" data-mp="2hdhr35odr98v" />
                  <div className="h-8 w-40 animate-pulse rounded-sm bg-brand-line" data-id="element-68" data-mp="2dc4kgtdgr20w" />
                </li>)}
            </ul>}
          {inventory.status === 'error' && <div className="px-6 py-12 text-center" data-id="element-69" data-mp="1kngwur58s0k6">
              <p className="text-sm text-brand-ink" data-id="element-70" data-mp="1yh857w78leeb">We couldn’t load {sport.noun} locations right now.</p>
              <button type="button" onClick={inventory.retry} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-skyDark" data-id="element-71" data-mp="1tp6xmnl0kbum">
                <RotateCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-72" data-mp="11z2684yu6ojw" /> Try again
              </button>
            </div>}
          {inventory.status === 'ready' && venues.length === 0 && <div className="px-6 py-12 text-center" data-id="element-73" data-mp="362x2g4k74hpp">
              <p className="font-semibold text-brand-navy" data-id="element-74" data-mp="361lwby3a2xvf">No locations match these filters.</p>
              <p className="mt-1 text-sm text-brand-muted" data-id="element-75" data-mp="3auy837muza6r">New {sport.noun} seasons are added often.</p>
              <button type="button" onClick={() => {
            setAreaFilter('all');
            setOpenOnly(false);
          }} className="mt-4 rounded-sm border border-brand-navy px-4 py-2 text-sm font-semibold text-brand-navy transition-colors duration-150 hover:bg-brand-navy hover:text-white" data-id="element-76" data-mp="3frf0ie13capn">
                Show all locations
              </button>
            </div>}
          {inventory.status === 'ready' && venues.length > 0 && <ul className="divide-y divide-brand-line" data-id="element-77" data-mp="7bv9llnw74dr">
              {venues.map((v, i) => <VenueCard key={v.id} venue={v} index={i} sport={sport} citySlug={city.slug} selected={selectedId === v.id} onSelect={() => select(v.id)} data-id="element-78" data-mp="cayen63vhalq" data-mpi={i} />)}
            </ul>}
        </div>
      </div>
      <ContentNote kind="dynamic" detail="Venues in metro offering this sport. Pins, status & links from backend." data-id="element-79" data-mp="1rvh5i3kt9cip" />
    </section>;
}